import { useAppStore } from '@/store/useAppStore.ts'
import { RangeSlider, ExpressionInput } from 'calc-controls'
import { useState } from 'react'
import { HelpIcon } from '@/components/shared/HelpIcon.tsx'
import { getChemistryPreset } from '@brt/shared-logic'
import { assessCycleLifePosition } from '@brt/shared-logic'

function estimateSoHFromCycles(cycles: number): number {
  if (cycles <= 0) return 1.0
  if (cycles >= 2000) return 0.5
  return Math.max(0.5, 1 - cycles * 0.00025)
}

export function SoHInput() {
  const soh = useAppStore((s) => s.battery.stateOfHealth)
  const ageCycles = useAppStore((s) => s.battery.ageCycles)
  const chemistry = useAppStore((s) => s.battery.chemistry)
  const updateBattery = useAppStore((s) => s.updateBattery)
  const [useCycleInput, setUseCycleInput] = useState(false)

  const preset = getChemistryPreset(chemistry)
  const cycleAssessment = assessCycleLifePosition(ageCycles, preset.cycleLifeRange, soh)

  const sohPercent = Math.round(soh * 100)
  const isLow = sohPercent < 70
  const isCritical = sohPercent < 50

  const handleSoHChange = (value: number) => {
    updateBattery('stateOfHealth', value / 100)
  }

  const handleCycleChange = (cycles: number) => {
    updateBattery('ageCycles', cycles)
    updateBattery('stateOfHealth', estimateSoHFromCycles(cycles))
  }

  const handleToggle = () => {
    setUseCycleInput(!useCycleInput)
  }

  return (
    <div className="soh-input">
      <div className="soh-input__header">
        <HelpIcon text="Remaining capacity relative to a new cell. Below 70% indicates significant degradation; below 50% means replacement is recommended.">
          <span className="soh-input__title">State of Health</span>
        </HelpIcon>
        <button
          type="button"
          className="soh-input__mode-toggle"
          onClick={handleToggle}
          aria-label={useCycleInput ? 'Switch to percentage input' : 'Switch to cycle count input'}
        >
          {useCycleInput ? 'Use %' : 'Use cycles'}
        </button>
      </div>

      {useCycleInput ? (
        <div className="soh-input__cycle-field">
          <ExpressionInput
            label="Cycle count"
            value={ageCycles || 0}
            onChange={handleCycleChange}
            min={0}
            max={5000}
            decimals={0}
          />
          <span className="soh-input__cycle-result">
            Estimated SoH: {sohPercent}%
          </span>
        </div>
      ) : (
        <RangeSlider
          label="State of Health"
          value={sohPercent}
          onChange={handleSoHChange}
          min={50}
          max={100}
          step={1}
          decimals={0}
          unit="%"
        />
      )}

      <div className={`soh-input__derating ${isLow ? 'soh-input__derating--warn' : ''}`}>
        Health derating: ×{soh.toFixed(2)} ({sohPercent}% SoH)
      </div>

      {cycleAssessment.lifePositionPercent != null && cycleAssessment.rangeMax != null && (
        <p className="soh-input__cycle-life" data-testid="cycle-life-position">
          {ageCycles} / {cycleAssessment.rangeMax} max cycles
          {' '}({cycleAssessment.lifePositionPercent.toFixed(1)}% of rated life)
        </p>
      )}
      {!cycleAssessment.sohPlausible && cycleAssessment.lifePositionPercent != null && (
        <p className="soh-input__warning soh-input__warning--caution" role="note">
          {cycleAssessment.note}
        </p>
      )}

      {isCritical && (
        <p className="soh-input__warning soh-input__warning--danger" role="alert">
          Battery critically degraded — replace recommended
        </p>
      )}
      {isLow && !isCritical && (
        <p className="soh-input__warning soh-input__warning--caution" role="alert">
          Battery health below 70% — significant capacity loss
        </p>
      )}
    </div>
  )
}
